import {
  InputType,
  QuestionKey,
  QuestionUnit,
  RequirementField,
  TimeUnit,
} from 'clients/Communication/Questionnaire.dto';
import { useContext } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';

export const useQuestion = () => {
  const { dictionary } = useContext(LocalizationContext);

  const questions: Record<QuestionKey, string> = {
    [QuestionKey.READY_FOR_JOB_OFFERS]: dictionary.areYouCurrentlyOpenToWork,
    [QuestionKey.CERTIFICATES_AND_LICENSES]: dictionary.doYouHaveFollowingCertificates,
    [QuestionKey.SALARY]: dictionary.isThisIncomeAcceptableForYou,
    [QuestionKey.SHIFTS]: dictionary.areYouAvailableForThisSchedule,
    [QuestionKey.AVAILABLE_DAYS]: dictionary.areYouAvailableForThisSchedule,
    [QuestionKey.LIMITATIONS_AND_PREFERENCES]: dictionary.areYouAbleToPerformTheseSpecificJobTasks,
    [QuestionKey.WORKING_HOURS_PER_WEEK]: dictionary.doTheseHoursPerWeekWorkForYou,
    [QuestionKey.LANGUAGES]: dictionary.doYouSpeakTheseLanguages,
  };

  const answeredQuestions: Record<QuestionKey, string | ((value: string) => string)> = {
    [QuestionKey.READY_FOR_JOB_OFFERS]: dictionary.areYouCurrentlyOpenToWork,
    [QuestionKey.CERTIFICATES_AND_LICENSES]: dictionary.doYouHaveAnyOtherCertificates,
    [QuestionKey.SALARY]: dictionary.whatIsYourDesiredSalaryRange,
    [QuestionKey.SHIFTS]: dictionary.doYouHaveAnyOtherShiftAvailable,
    [QuestionKey.AVAILABLE_DAYS]: dictionary.whatAreYouAvailableDays,
    [QuestionKey.LIMITATIONS_AND_PREFERENCES]: dictionary.areThereAnyOtherTasksYouCanDo,
    [QuestionKey.WORKING_HOURS_PER_WEEK]: dictionary.howManyHoursPerWeekWouldYouLikeToWork,
    [QuestionKey.LANGUAGES]: dictionary.whichLanguagesDoYouSpeak,
  };

  const timeUnitQuestions: Record<TimeUnit, string> = {
    [TimeUnit.WEEK]: dictionary.week,
    [TimeUnit.DAY]: dictionary.day,
    [TimeUnit.HOUR]: dictionary.hour,
    [TimeUnit.MONTH]: dictionary.month,
    [TimeUnit.YEAR]: dictionary.year,
  };

  const timeUnitFrequencies: Record<TimeUnit, string> = {
    [TimeUnit.WEEK]: dictionary.weekly,
    [TimeUnit.DAY]: dictionary.daily,
    [TimeUnit.HOUR]: dictionary.hourly,
    [TimeUnit.MONTH]: dictionary.monthly,
    [TimeUnit.YEAR]: dictionary.yearly,
  };

  const getTimeUnitQuestion = (unit?: QuestionUnit): string => {
    return timeUnitQuestions[unit as TimeUnit] || '';
  };

  const getTimeUnitFrequency = (unit?: QuestionUnit): string => {
    return timeUnitFrequencies[unit as TimeUnit] || '';
  };

  const getQuestionText = (questionKey: QuestionKey, isAnswered?: boolean, extra_params?: string): string => {
    if (isAnswered && extra_params && typeof answeredQuestions[questionKey] === 'function') {
      return answeredQuestions[questionKey](extra_params);
    }
    if (isAnswered && typeof answeredQuestions[questionKey] === 'string') {
      return answeredQuestions[questionKey];
    }
    return questions[questionKey];
  };

  const joinPrimaryAndSecondarySuggestions = (
    primarySuggestions: string[],
    secondarySuggestions: string[],
  ): string[] => {
    const primarySuggestionsSet = primarySuggestions.join(', ');
    const secondarySuggestionsSet = secondarySuggestions.join(', ');
    return [primarySuggestionsSet, secondarySuggestionsSet].filter(Boolean);
  };

  const getSuggestions = (requirement: RequirementField<any>): string[] => {
    if (!requirement.job_requirements) {
      return [];
    }

    switch (requirement.input_type) {
      case InputType.RANGE_SLIDER:
      case InputType.RANGE_INPUT:
        if (requirement.job_requirements.min === requirement.job_requirements.max) {
          return [`${requirement.job_requirements.min}`];
        }
        return [`${requirement.job_requirements.min} - ${requirement.job_requirements.max}`];
      case InputType.ARRAY:
        return requirement.job_requirements;
      case InputType.STRING:
        return [requirement.job_requirements];
      case InputType.BOOLEAN:
        return [requirement.job_requirements ? dictionary.yes : dictionary.no];
      default:
        return [];
    }
  };

  return {
    getQuestionText,
    getSuggestions,
    joinPrimaryAndSecondarySuggestions,
    getTimeUnitQuestion,
    getTimeUnitFrequency,
  };
};
